import type React from "react"
import { useCallback, useMemo, useRef, useState } from "react"
import styled, { useTheme } from "styled-components"
import type { BaseProps, BorderProps } from "../types/props"
import { getBorderStyle } from "../util/props/border"
import { overloadColor, validateHex } from "../util/style"

interface WrapperProps extends BorderProps {
  width: string
  height: string

  color: string
  background: string
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  width: ${(props) => props.width};
  height: ${(props) => props.height};

  input {
    width: ${(props) => props.width};
    height: 100%;
    padding-left: 4px;

    color: ${(props) => overloadColor(props.color)};
    background: ${(props) => overloadColor(props.background)};
    border: solid ${(props) => overloadColor(props.borderColor)};
    ${getBorderStyle}
    border-left-width: 0px;
    border-radius: 0 ${(props) => props.theme.sizes.defaultBorderRadius}
      ${(props) => props.theme.sizes.defaultBorderRadius} 0;

    font-size: 0.8rem;

    &:focus {
      outline: none;
    }
  }
`

interface ColorBoxProps extends BorderProps {
  color: string
  valid: boolean
}
const ColorBox = styled.div<ColorBoxProps>`
  position: relative;
  width: 42px;
  height: 100%;

  ${getBorderStyle}
  border-right-width: 0px;
  border-radius: ${(props) => props.theme.sizes.defaultBorderRadius} 0 0
    ${(props) => props.theme.sizes.defaultBorderRadius};

  cursor: pointer;

  &::after {
    content: '${(props) => (props.valid ? "" : "\f00d")}';
    font-family: 'Font Awesome 5 Pro';
    color: ${(props) => props.theme.colors.error};
    display: flex;
    justify-content: center;
    align-items: center;

    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: ${(props) => (props.valid ? props.color : "inherit")};
    border-radius: ${(props) => props.theme.sizes.defaultBorderRadius} 0 0
      ${(props) => props.theme.sizes.defaultBorderRadius};
  }
`

export interface ColorPickerProps extends BorderProps, BaseProps {
  value: string

  width?: string
  fullWidth?: boolean
  height?: string
  color?: string
  background?: string

  onClick?: (evt: React.MouseEvent<HTMLElement>) => void
  onColorBoxClick?: (evt: React.MouseEvent<HTMLDivElement>) => void
  onChange: (value: string) => void
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  className,
  id,

  value,

  width = "auto",
  fullWidth = false,
  height = "32px",
  color = "text.dark",
  background = "white",

  border,
  borderWidth,
  borderStyle,
  borderColor,

  onClick = () => void 0,
  onColorBoxClick = () => void 0,
  onChange,
}) => {
  const pickerRef = useRef<HTMLInputElement | null>(null)
  const theme = useTheme()

  const [valid, setValid] = useState(validateHex(value) !== null)

  const withHex = (clr: string) => "#" + clr.split("#").pop()

  const handleColorBoxClick = useCallback(
    (evt: React.MouseEvent<HTMLDivElement>) => {
      if (!pickerRef.current) return

      onColorBoxClick(evt)
      if (evt.defaultPrevented) return

      pickerRef.current.click()
    },
    [onColorBoxClick],
  )

  const handleChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const clr = withHex(evt.target.value)
      if (!/^#[a-f0-9]*$/i.test(clr)) return

      setValid(validateHex(clr) !== null)
      onChange(clr)
    },
    [onChange],
  )

  const parsedBorder = useMemo(
    () => border ?? theme.layout.defaultBorder,
    [border, theme.layout.defaultBorder],
  )

  return (
    <Wrapper
      className={className}
      id={id}
      width={fullWidth ? "100%" : width}
      height={height}
      border={parsedBorder}
      borderWidth={borderWidth}
      borderStyle={borderStyle}
      borderColor={borderColor}
      color={color}
      background={background}
      onClick={onClick}
    >
      <ColorBox
        color={value}
        valid={valid}
        border={parsedBorder}
        borderWidth={borderWidth}
        borderStyle={borderStyle}
        borderColor={borderColor}
        onClick={handleColorBoxClick}
      />
      <input value={value} maxLength={9} onChange={handleChange} />
      <input
        ref={pickerRef}
        hidden
        type="color"
        value={value}
        onChange={handleChange}
      />
    </Wrapper>
  )
}
